import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FsComboOption } from '../../common/fs-combobox/fs-combobox.component';
import { SplitType } from '../../invitations/models/invitation.model';
import { Session } from '../models/session.model';

const SPLIT_OPTIONS: { id: SplitType; name: string }[] = [
  { id: 'similar', name: 'Similar Preference' },
  { id: 'diverse_exact', name: 'Diverse Preference: Exact' },
];

/**
 * Create / re-split / edit — one modal, three modes, same form with different copy.
 * Two components would drift. Task 9 (the session panel) opens this and calls the mock
 * service with whatever `confirmed` emits.
 */
@Component({
  selector: 'app-session-create-modal',
  templateUrl: './session-create-modal.component.html',
  styleUrls: ['./session-create-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionCreateModalComponent implements OnInit {
  @Input() mode: 'create' | 'resplit' | 'edit' = 'create';
  @Input() eligibleCount = 0;
  @Input() totalCount = 0;
  @Input() session?: Session | null;

  @Output() confirmed = new EventEmitter<{ name: string; expiresAt?: string; splitType: SplitType; groupCount: number }>();
  @Output() cancelled = new EventEmitter<void>();

  name = '';
  expiresAt: Date | null = null;
  splitType: SplitType = 'diverse_exact';
  groupCount = 4;
  tried = false;

  readonly splitOptions: FsComboOption[] = SPLIT_OPTIONS;
  // Same calendar as the wizard / Invitations (date-only; expiry is a whole-day cutoff).
  readonly bsConfig = { isAnimated: true, dateInputFormat: 'MM/DD/YYYY', showWeekNumbers: false };

  ngOnInit(): void {
    if (this.session) {
      this.name = this.session.name;
      this.expiresAt = this.session.expiresAt ? new Date(this.session.expiresAt) : null;   // ISO → Date
      this.splitType = this.session.splitType;
      this.groupCount = Math.max(1, this.session.groups.length);
    }
  }

  get title(): string {
    return this.mode === 'create' ? 'Create activity group'
         : this.mode === 'resplit' ? 'Re-split activity group'
         : 'Edit activity group';
  }
  get subtitle(): string {
    return this.mode === 'edit' ? 'Rename it or change when it ends'
         : "Regroup the activity group's participants";
  }
  get cta(): string {
    return this.mode === 'create' ? 'Create activity group'
         : this.mode === 'resplit' ? 'Re-split'
         : 'Save changes';
  }
  /** Editing name/expiry must not touch the groups, so the split fields are hidden. */
  get showSplitFields(): boolean { return this.mode !== 'edit'; }
  /** Both splits honour the requested count; only the edit mode hides the split fields. */
  get showGroupCount(): boolean { return this.showSplitFields; }

  get error(): string {
    if (!this.name.trim()) { return 'Give the activity a name.'; }
    // No expiry check: expiry is optional — cleared means the groups never expire.
    if (this.showGroupCount) {
      if (!this.groupCount || this.groupCount < 1 || Math.floor(this.groupCount) !== this.groupCount) {
        return 'Enter a whole number of groups, at least 1.';
      }
      if (this.mode === 'create' && this.groupCount > this.eligibleCount) {
        return `Only ${this.eligibleCount} participants are eligible — that's fewer than ${this.groupCount} groups.`;
      }
      // On re-split, runSplit deals over the session's original snapshot (session.participantIds),
      // not the live eligible count — so the guard must compare against the snapshot size too,
      // or a count above it silently yields fewer groups than asked (empty groups are dropped).
      if (this.mode === 'resplit' && this.session && this.groupCount > this.session.participantIds.length) {
        return `The activity has ${this.session.participantIds.length} participants — that's fewer than ${this.groupCount} groups.`;
      }
    }
    return '';
  }

  /**
   * bsDatepicker gives local midnight; store END-of-day so the group stays active all that day.
   * Cleared returns `undefined` — the store's single "never expires" representation, never `''`.
   */
  private toExpiryIso(d: Date | null): string | undefined {
    if (!d) { return undefined; }
    const day = new Date(d);
    day.setHours(23, 59, 59, 999);
    return day.toISOString();
  }

  submit(): void {
    this.tried = true;
    if (this.error) { return; }
    this.confirmed.emit({
      name: this.name.trim(),
      expiresAt: this.toExpiryIso(this.expiresAt),
      splitType: this.splitType,
      groupCount: this.groupCount,
    });
  }
}
